import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const METHOD_COLORS = {
  GET: 'hsl(220, 70%, 50%)',
  POST: 'hsl(160, 60%, 45%)',
  PUT: 'hsl(30, 80%, 55%)',
  DELETE: 'hsl(0, 72%, 51%)',
  PATCH: 'hsl(270, 60%, 55%)'
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;

  return (
    <div className="rounded-md border bg-popover p-3 text-popover-foreground shadow-md">
      <p className="mb-1 text-sm font-medium font-mono">{d.route}</p>
      <p className="text-xs text-muted-foreground">
        Total: <span className="font-medium text-foreground">{d.total_count.toLocaleString()}</span>
      </p>
      <p className="text-xs text-muted-foreground">
        Avg Duration: <span className="font-medium text-foreground">{d.avg_duration_ms}ms</span>
      </p>
      {d.methods && (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {Object.entries(d.methods)
            .sort((a, b) => b[1] - a[1])
            .map(([method, count]) => (
              <span key={method} className="text-xs font-medium" style={{ color: METHOD_COLORS[method] || 'hsl(var(--muted-foreground))' }}>
                {method}: {count}
              </span>
            ))}
        </div>
      )}
    </div>
  );
};

const VolumeChart = ({ data }) => {
  const chartData = useMemo(() => {
    return data.map((item) => {
      const methods = item.methods || {};
      const dominant = Object.entries(methods).sort((a, b) => b[1] - a[1]);
      const isMixed = dominant.length > 1 && dominant[0][1] < item.total_count * 0.7;
      return {
        ...item,
        dominantMethod: isMixed ? 'MIXED' : dominant[0]?.[0] || 'GET'
      };
    });
  }, [data]);

  const chartHeight = Math.max(600, chartData.length * 28);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Request Volume by Route</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 0 }} barCategoryGap="20%">
              <XAxis type="number" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis
                dataKey="route"
                type="category"
                width={200}
                tick={{ fontSize: 11, fontFamily: 'monospace', fill: 'hsl(var(--foreground))' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted) / 0.5)' }} />
              <Bar dataKey="total_count" radius={[0, 4, 4, 0]}>
                {chartData.map((entry) => {
                  const color = entry.dominantMethod === 'MIXED' ? 'hsl(var(--muted-foreground))' : METHOD_COLORS[entry.dominantMethod] || 'hsl(var(--muted-foreground))';
                  return <Cell key={entry.route} fill={color} opacity={0.85} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex items-center justify-center gap-4">
          {Object.entries(METHOD_COLORS).map(([method, color]) => (
            <div key={method} className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
              <span className="text-xs text-muted-foreground">{method}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="h-2.5 w-2.5 rounded-sm bg-muted-foreground" />
            <span className="text-xs text-muted-foreground">Mixed</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VolumeChart;
